import { _ as _defineProperty } from './defineProperty-2d1aa32d.js';
import { _ as _asyncToGenerator, r as regenerator } from './index-e4593f56.js';
import { _ as _slicedToArray } from './slicedToArray-a6a95674.js';
import { _ as _objectWithoutProperties } from './objectWithoutProperties-2f896e18.js';
import { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { useAuthContext, authStatus } from '@picsart/growth-rc/AuthContext';
import analyticsService from '@picsart/rc/services/analytics';
import createContext from '@picsart/rc/createContext';
import IS_BROWSER from '@picsart/rc/isBrowser';
import useMount from '@picsart/rc/useMount';
import { U as UserSubscriptionStatesEnum } from './userSubscriptionModel-47e03fd5.js';
import { DEFAULT_PLUS_TIER_SCOPE_ID } from './constants/index.js';
import { checkRedirectAction } from './paymentsService/index.js';
import { fetchUserSubscription } from './userService/index.js';
import useImpactIdentify from './useImpactIdentify/index.js';
import useTokenData from './useTokenData/index.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["userSubscription", "scope", "children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var Resolved = UserSubscriptionStatesEnum.Resolved,
  Pending = UserSubscriptionStatesEnum.Pending,
  Failed = UserSubscriptionStatesEnum.Failed;
var _createContext = createContext({
    isOpen: false,
    setIsOpen: function setIsOpen(isOpen) {
      return isOpen;
    }
  }, 'CheckoutModalContext'),
  CheckoutModalContext = _createContext.context,
  useCheckoutModalContext = _createContext.useContext;
function CheckoutModalProvider(_ref) {
  var initialUserSubscription = _ref.userSubscription,
    _ref$scope = _ref.scope,
    scope = _ref$scope === void 0 ? DEFAULT_PLUS_TIER_SCOPE_ID : _ref$scope,
    children = _ref.children,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useAuthContext = useAuthContext(),
    status = _useAuthContext.status,
    tokenData = _useAuthContext.tokenData;
  var _useTokenData = useTokenData(),
    getAccessToken = _useTokenData.getAccessToken;
  var isInitialFetchRef = useRef(true);
  var scopeIdRef = useRef(scope);
  var queryParams = useMemo(function () {
    return new URLSearchParams(IS_BROWSER ? window.location.search : undefined);
  }, []);
  var is3dSecureFlow = useMemo(function () {
    return !!(queryParams.get('paymentFlow') && checkRedirectAction());
  }, [queryParams]);
  var _useState = useState(initialUserSubscription ? Resolved : Pending),
    _useState2 = _slicedToArray(_useState, 2),
    userSubscriptionState = _useState2[0],
    setUserSubscriptionState = _useState2[1];
  var _useState3 = useState(initialUserSubscription),
    _useState4 = _slicedToArray(_useState3, 2),
    userSubscription = _useState4[0],
    setUserSubscription = _useState4[1];
  var updateUserSubscription = useCallback(/*#__PURE__*/_asyncToGenerator(/*#__PURE__*/regenerator.mark(function _callee() {
    var _ref3,
      scopeId,
      accessToken,
      _yield$fetchUserSubsc,
      _status,
      data,
      _args = arguments;
    return regenerator.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          _ref3 = _args.length > 0 && _args[0] !== undefined ? _args[0] : {}, scopeId = _ref3.scopeId;
          accessToken = getAccessToken();
          if (scopeId) {
            // update with given scopeId
            scopeIdRef.current = scopeId;
          }
          _context.prev = 3;
          _context.next = 6;
          return fetchUserSubscription({
            expand: true,
            scopeId: scopeIdRef.current
          }, accessToken);
        case 6:
          _yield$fetchUserSubsc = _context.sent;
          _status = _yield$fetchUserSubsc.status;
          data = _yield$fetchUserSubsc.data;
          if (_status === 'success') {
            setUserSubscription(data);
            setUserSubscriptionState(Resolved);
          } else {
            setUserSubscriptionState(Failed);
          }
          _context.next = 15;
          break;
        case 12:
          _context.prev = 12;
          _context.t0 = _context["catch"](3);
          setUserSubscriptionState(Failed);
        case 15:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[3, 12]]);
  })), [getAccessToken]);
  useEffect(function () {
    if (isInitialFetchRef.current && initialUserSubscription) {
      // don't fetch userSubscription on mount if provided
      isInitialFetchRef.current = false;
      return;
    }
    if (isInitialFetchRef.current && tokenData !== null && tokenData !== void 0 && tokenData.access_token) {
      isInitialFetchRef.current = false;
      updateUserSubscription();
    }
  }, [initialUserSubscription, tokenData, updateUserSubscription]);
  useEffect(function () {
    if (status === authStatus.failed) {
      isInitialFetchRef.current = true; // reset on logout
      setUserSubscriptionState(Resolved); // logged-out user => resolved but with undefined value
      analyticsService.pushDataLayer({
        pulse: 'state',
        tier_id: null
      });
    }
  }, [status]);
  useEffect(function () {
    if (userSubscription) {
      var tier_id = userSubscription.is_subscribed ? userSubscription.tier_meta.id : 'free';
      analyticsService.pushDataLayer({
        pulse: 'state',
        tier_id: tier_id
      });
      analyticsService.pushDataLayer({
        pulse: 'state',
        device_last_tier_id: tier_id
      });
      analyticsService.pushDataLayer({
        pulse: 'attribute',
        last_subscription_package_name: tier_id
      });
    }
  }, [userSubscription]);
  useImpactIdentify(userSubscriptionState);
  useMount(function () {
    is3dSecureFlow && props.setIsOpen(true);
  });
  var contextValue = useMemo(function () {
    return _objectSpread(_objectSpread({}, props), {}, {
      is3dSecureFlow: is3dSecureFlow,
      userSubscription: userSubscription,
      userSubscriptionState: userSubscriptionState,
      updateUserSubscription: updateUserSubscription
    });
  }, [props, updateUserSubscription, userSubscription, userSubscriptionState, is3dSecureFlow]);
  return /*#__PURE__*/jsx(CheckoutModalContext.Provider, {
    value: contextValue,
    children: children
  });
}

export { CheckoutModalContext as C, CheckoutModalProvider as a, useCheckoutModalContext as u };
//# sourceMappingURL=CheckoutModalContext-fa8f6d02.js.map
